import React, { useState } from 'react';
import { Box, Typography, Menu, MenuItem } from '@mui/material';
import CreateNewFolderRoundedIcon from '@mui/icons-material/CreateNewFolderRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Folder } from '../../services/folders_api';
import { FolderGlyph, folderColor } from './folderVisuals';

interface SelectionToolbarProps {
  count: number;
  folders: Folder[];
  onAddToFolder: (folderId: number) => void;
  onClear: () => void;
  onDone: () => void;
}

// 複数選択モード時、フォルダバーの位置に出るツールバー。
const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  count, folders, onAddToFolder, onClear, onDone,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const disabled = count === 0;

  const pillBase = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1.1,
    py: 0.5,
    borderRadius: '999px',
    cursor: 'pointer',
    userSelect: 'none' as const,
    flexShrink: 0,
    transition: 'all .15s ease',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        py: 0.4,
        px: 0.5,
        borderRadius: '999px',
        backgroundColor: 'rgba(217,255,0,0.06)',
        border: '1px solid rgba(217,255,0,0.2)',
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 0 },
        scrollbarWidth: 'none',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.76rem',
          fontWeight: 700,
          color: 'primary.main',
          pl: 0.75,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {count}件選択中
      </Typography>

      <Box sx={{ width: '1px', height: 16, backgroundColor: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />

      {/* フォルダに追加 */}
      <Box
        onClick={(e) => { if (!disabled) setMenuAnchor(e.currentTarget); }}
        sx={{
          ...pillBase,
          backgroundColor: disabled ? 'rgba(255,255,255,0.03)' : 'primary.main',
          color: disabled ? 'rgba(255,255,255,0.3)' : '#0a0a0a',
          cursor: disabled ? 'default' : 'pointer',
          fontWeight: 700,
          '&:hover': { filter: disabled ? 'none' : 'brightness(1.1)' },
        }}
      >
        <CreateNewFolderRoundedIcon sx={{ fontSize: '0.95rem' }} />
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
          フォルダに追加
        </Typography>
      </Box>

      {/* 全解除 */}
      <Box
        onClick={disabled ? undefined : onClear}
        sx={{
          ...pillBase,
          backgroundColor: 'rgba(255,255,255,0.04)',
          color: disabled ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.6)',
          cursor: disabled ? 'default' : 'pointer',
          '&:hover': { backgroundColor: disabled ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)' },
        }}
      >
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 500, lineHeight: 1, whiteSpace: 'nowrap' }}>
          全解除
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minWidth: 4 }} />

      {/* 完了 */}
      <Box
        onClick={onDone}
        sx={{
          ...pillBase,
          backgroundColor: 'rgba(255,255,255,0.06)',
          color: 'text.primary',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
        }}
      >
        <CheckRoundedIcon sx={{ fontSize: '0.95rem' }} />
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, lineHeight: 1 }}>完了</Typography>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { mt: 0.5, minWidth: 200, maxHeight: 320, borderRadius: '10px' } }}
      >
        {folders.length === 0 ? (
          <MenuItem disabled sx={{ fontSize: '0.78rem' }}>フォルダがありません</MenuItem>
        ) : (
          folders.map((folder) => {
            const color = folderColor(folder.color);
            return (
              <MenuItem
                key={folder.id}
                onClick={() => { onAddToFolder(folder.id); setMenuAnchor(null); }}
                sx={{ gap: 1, fontSize: '0.82rem' }}
              >
                <FolderGlyph iconKey={folder.icon} sx={{ fontSize: '1rem', color }} />
                <Typography sx={{ flex: 1, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {folder.name}
                </Typography>
                <Typography sx={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>
                  {folder.item_count}
                </Typography>
              </MenuItem>
            );
          })
        )}
      </Menu>
    </Box>
  );
};

export default SelectionToolbar;
